import { NodeId } from '@candulabs/craft';
import { useMemo } from 'react';

import { useMultiNodes } from 'craft/hooks/useMultiNodes';
import { useStyleguideCss } from 'craft/state/styleguide/selectors';
import { useViewportBreakpoint } from 'craft/state/viewport/selectors';
import { CssSelectorId } from 'craft/types';

import { createStyleguideHierachy, StylesHierarchy } from './createStyleguideHierarchy';
import { useResponsiveCss } from './useResponsiveCss';

const SELECTOR_EMPTY_OBJ = {};
const CSS_EMPTY_OBJ = {};

Object.freeze(CSS_EMPTY_OBJ);
Object.freeze(SELECTOR_EMPTY_OBJ);

interface Options {
  cssSelectorId?: CssSelectorId;
  commonProperty?: string;
}

/**
 *
 * useCss hooks is a single hook that should be able to replace and incorporate
 * all the other hook usages into a simple hook.
 *
 * At a high level, styles are resolved in the following way:
 * 1. Styleguide. These are the base style.
 *  If type=multiple, styleguide returns no styles
 * 2. Node base styles. Either node.css or the base object.
 * 3. Responsive styles.
 * 4. PseudoClass styles (not implemented yet).
 */
export const useCss = (nodeIds: NodeId[], options: Options = {}) => {
  const viewport = useViewportBreakpoint();
  const { cssSelectorId = CssSelectorId.Root } = options;
  const { type, variant } = useMultiNodes(nodeIds, (node) => ({
    variant:
      node?.data?.custom.custom?.variation ||
      node?.data?.props?.variant ||
      node?.data?.custom?.custom?.variant,
  }));

  // very, very hacky...
  // TODO: clean up!
  let resolvedVariant = cssSelectorId === CssSelectorId.TypographyHrefChildren ? null : variant;
  let resolvedType = type;
  if (type === 'Typography' && variant === 'P') {
    resolvedVariant = 'base';
  }
  if (type === 'ListItem') {
    // we need to resolve to Typography type in order to get styleguide styles for ListItem
    resolvedType = 'Typography';
  }

  // again, very very hacky, but not in any models so we can live with this for now...
  if (type?.includes('Form')) {
    resolvedType = 'Form';
  }

  const styleguideCss = useStyleguideCss(resolvedType, resolvedVariant, cssSelectorId);

  const {
    isRootViewportCss,
    responsiveCss,
    setResponsiveCss,
    lgCss,
    mdCss,
    smCss,
  } = useResponsiveCss(nodeIds, options);

  const css = useMemo(() => ({ ...styleguideCss, ...responsiveCss }), [
    cssSelectorId,
    responsiveCss,
    styleguideCss,
  ]);

  const stylesHierarchy: StylesHierarchy = useMemo(() => {
    // @ts-expect-error
    return createStyleguideHierachy({ css, styleguideCss, lgCss, mdCss, smCss, viewport });
  }, [css, styleguideCss, lgCss, mdCss, smCss, viewport]);

  return { type, isRootViewportCss, styleguideCss, css, setCss: setResponsiveCss, stylesHierarchy };
};
